// Right-pane evidence panel.
//
// Two sub-panes:
//   1. Validation — issues from POST /api/sim/scenarios/:id/validation.
//      Blocking first, then warnings. Every warning has an
//      "Acknowledge" button that POSTs to the acknowledgements endpoint
//      and immediately marks the row green.
//   2. Provenance — list of every parameter in the currently-selected
//      section with its source, confidence, updated_at, and citation.
//
// The parent page debounces re-validation at 500 ms after any
// parameter edit; this component just renders whatever the parent
// hands it.
import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, ShieldAlert, ShieldCheck } from "lucide-react";
import SpinnerButton from "@/components/SpinnerButton";
import { sectionById } from "@/components/workbench/sections";

function Header({ title, count, tone }) {
    const cls =
        tone === "danger"
            ? "text-ux-danger"
            : tone === "warning"
            ? "text-ux-warning"
            : "text-ux-success";
    return (
        <div
            className="flex items-center justify-between px-4 py-2 border-b border-ux-border-1"
            data-testid={`workbench-evidence-heading-${tone}`}
        >
            <h3 className={`text-[11px] font-mono uppercase tracking-[0.18em] ${cls}`}>
                {title}
            </h3>
            <span
                className="text-[11px] text-ux-text-3 font-mono tabular-nums"
                data-testid={`workbench-evidence-count-${tone}`}
            >
                {count}
            </span>
        </div>
    );
}

function IssueRow({ issue, rules, alreadyAcked, onAcknowledge, busyRuleId }) {
    const rule = rules.find((r) => r.rule_id === issue.rule_id);
    const blocking = issue.severity === "blocking";
    const Icon = blocking ? ShieldAlert : AlertTriangle;
    return (
        <div
            className="px-4 py-3 border-b border-ux-border-1 last:border-b-0"
            data-testid={`workbench-evidence-issue-${issue.rule_id}`}
        >
            <div className="flex items-start gap-2">
                <Icon
                    className={
                        blocking
                            ? "w-4 h-4 text-ux-danger mt-0.5 shrink-0"
                            : "w-4 h-4 text-ux-warning mt-0.5 shrink-0"
                    }
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13px] font-medium text-ux-text-1">
                            {rule?.title || issue.rule_id}
                        </span>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                            {rule?.scope}
                        </span>
                    </div>
                    <p className="mt-0.5 text-[12px] text-ux-text-2 break-words">
                        {issue.message}
                    </p>
                    {issue.path && (
                        <p className="mt-1 text-[11px] font-mono text-ux-text-3 truncate">
                            {issue.path}
                        </p>
                    )}
                    {!blocking && (
                        <div className="mt-2">
                            {alreadyAcked ? (
                                <span
                                    className="inline-flex items-center gap-1 text-[11px] text-ux-success"
                                    data-testid={`workbench-evidence-acked-${issue.rule_id}`}
                                >
                                    <CheckCircle2 className="w-3 h-3" /> Acknowledged
                                </span>
                            ) : (
                                <SpinnerButton
                                    variant="ghost"
                                    loading={busyRuleId === issue.rule_id}
                                    onClick={() => onAcknowledge(issue.rule_id)}
                                    testId={`workbench-evidence-ack-${issue.rule_id}`}
                                >
                                    Acknowledge
                                </SpinnerButton>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ProvenanceList({ scenario, activeSection }) {
    const section = sectionById(activeSection);
    const bucket = scenario?.parameters?.[activeSection] || {};
    const entries = Object.entries(bucket);
    if (!section) return null;
    if (!entries.length) {
        return (
            <p
                className="px-4 py-3 text-[12px] text-ux-text-3 font-mono"
                data-testid="workbench-evidence-provenance-empty"
            >
                No parameters set in {section.label.toLowerCase()} yet.
            </p>
        );
    }
    return (
        <ul className="divide-y divide-ux-border-1" data-testid="workbench-evidence-provenance">
            {entries.map(([name, p]) => (
                <li key={name} className="px-4 py-2.5 text-[12px]">
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-ux-text-1 font-medium truncate">{name}</span>
                        {p.source && (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                                {p.source}
                            </span>
                        )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-ux-text-3 font-mono text-[11px] tabular-nums">
                        <span>= {p.value ?? "—"}</span>
                        {p.unit && <span>{p.unit}</span>}
                        {p.confidence && (
                            <span className="ml-auto">{p.confidence}</span>
                        )}
                    </div>
                </li>
            ))}
        </ul>
    );
}

export default function WorkbenchEvidence({
    scenario,
    activeSection,
    validation,
    validating,
    rules,
    onAcknowledge,
}) {
    const [busyRuleId, setBusyRuleId] = useState(null);

    const blocking = validation?.issues?.filter((i) => i.severity === "blocking") || [];
    const warnings = validation?.issues?.filter((i) => i.severity === "warning") || [];

    const ackedRuleIds = useMemo(
        () =>
            new Set(
                (scenario?.acknowledgements || []).map((a) => a.rule_id),
            ),
        [scenario?.acknowledgements],
    );

    const handleAck = async (rule_id) => {
        setBusyRuleId(rule_id);
        try {
            await onAcknowledge?.(rule_id);
        } finally {
            setBusyRuleId(null);
        }
    };

    return (
        <aside
            className="h-full overflow-y-auto border-l border-ux-border-1 bg-ux-bg-1"
            aria-label="Evidence and validation"
            data-testid="workbench-evidence"
        >
            <div
                className="px-4 py-3 border-b border-ux-border-1 flex items-center gap-2"
                data-testid="workbench-evidence-top"
            >
                {validation?.runnable ? (
                    <ShieldCheck className="w-4 h-4 text-ux-success" aria-hidden="true" />
                ) : (
                    <ShieldAlert className="w-4 h-4 text-ux-warning" aria-hidden="true" />
                )}
                <h3 className="text-[13px] font-medium text-ux-text-1">Evidence</h3>
                {validating && (
                    <span
                        className="ml-auto text-[11px] font-mono text-ux-text-3"
                        data-testid="workbench-evidence-validating"
                    >
                        revalidating…
                    </span>
                )}
            </div>

            <Header title="Blocking" count={blocking.length} tone="danger" />
            {blocking.length === 0 ? (
                <p
                    className="px-4 py-3 text-[12px] text-ux-text-3 font-mono"
                    data-testid="workbench-evidence-no-blocking"
                >
                    No blocking issues.
                </p>
            ) : (
                blocking.map((issue) => (
                    <IssueRow
                        key={`b-${issue.rule_id}-${issue.path || ""}`}
                        issue={issue}
                        rules={rules}
                        alreadyAcked={false}
                        onAcknowledge={() => {}}
                        busyRuleId={busyRuleId}
                    />
                ))
            )}

            <Header title="Warnings" count={warnings.length} tone="warning" />
            {warnings.length === 0 ? (
                <p
                    className="px-4 py-3 text-[12px] text-ux-text-3 font-mono"
                    data-testid="workbench-evidence-no-warnings"
                >
                    No warnings.
                </p>
            ) : (
                warnings.map((issue) => (
                    <IssueRow
                        key={`w-${issue.rule_id}-${issue.path || ""}`}
                        issue={issue}
                        rules={rules}
                        alreadyAcked={ackedRuleIds.has(issue.rule_id)}
                        onAcknowledge={handleAck}
                        busyRuleId={busyRuleId}
                    />
                ))
            )}

            <Header title="Provenance" count={Object.keys(scenario?.parameters?.[activeSection] || {}).length} tone="ok" />
            <ProvenanceList scenario={scenario} activeSection={activeSection} />
        </aside>
    );
}
